import React, { useEffect, useState } from 'react';
import '../App.css';
import Footer from '../components/Footer';

const BlogFallPreventionPage: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [storyImage, setStoryImage] = useState<string>('');

  useEffect(() => {
    // Fetch the story image
    fetch('http://localhost:4000/api/images')
      .then(res => res.json())
      .then(data => {
        const image = data.images.find((img: any) => 
          img.filename === 'Fall-Prevention-Medical-Alert-Sytem-at-Alert1-.jpeg'
        );
        if (image) {
          setStoryImage(`${API_URL}/api/images/${image.id}`);
        }
      })
      .catch(err => console.error('Error fetching image:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-container">
      {/* Blog Hero Section */}
      <section className="blog-hero-section">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">Fall Prevention for Aging Parents: A Complete Guide</h1>
          
          <div className="blog-meta">
            <span className="blog-author">By Shayne Fitz-Coy</span>
            <span className="blog-date">June 08, 2025</span>
          </div>
          
          <p className="blog-subtitle">By Shayne Fitz-Coy | co-Founder, Sabot Family Companies</p>
          
          <div className="blog-disclosure">
            <strong>Disclosure:</strong> Shayne Fitz-Coy is a former investor in Alert1, and a certified aging in place specialist with inside industry perspective on fall prevention risk mitigation. This guide presents evidence-based information from multiple sources.
          </div>
          
          <div className="blog-action-plan">
            <h2 className="action-plan-title">Start Here: Your 5-Step Action Plan</h2>
            
            <div className="action-plan-warning">
              <strong>Warning:</strong> This guide is long. Before diving into the full thing, here are the five most impactful steps you can take this week to minimize potential fall hazards for your aging parent:
            </div>
            
            <ol className="action-plan-list">
              <li>
                <strong>Tonight:</strong> Ask: "What would it mean to you to age on your own terms?"
              </li>
              <li>
                <strong>Tomorrow:</strong> Check the bathroom for grab bars and nonskid mats.
              </li>
              <li>
                <strong>Within 3 Days:</strong> Review all medications with their pharmacist or health care providers.
              </li>
              <li>
                <strong>This Weekend:</strong> Clear one walking path in the home completely. Pay particular attention to electrical cords.
              </li>
              <li>
                <strong>Within 2 Weeks:</strong> Schedule a balance assessment with their primary care doctor.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Blog Content Section */}
      <section className="blog-content-section">
        <div className="blog-content-container">
          
          {/* Personal Story Section with Image */}
          <div className="blog-story-section">
            <h2>Understanding How To Prevent Falls For Older Adults</h2>
            
            <p>
              The call came at 2:47 AM. My grandmother had a stroke and had fallen in her bathroom in Kingston, 
              Jamaica. Or she had a fall and then a stroke. We don't really know. Falls are the leading cause 
              of injury and death among older adults.
            </p>
            
            <p>
              For hours, she lay there, unable to get help. She had no history of stroke. She'd had no previous 
              falls or serious injury until then.
            </p>
            
            {storyImage && (
              <div className="blog-story-image-wrapper">
                <img 
                  src={storyImage} 
                  alt="Understanding How To Prevent Falls For Older Adults" 
                  className="blog-story-image"
                />
              </div>
            )}
          </div>

          {/* Statistics Section */}
          <div className="blog-statistics-section">
            <h2>The Numbers Tell Only Part of the Story on Falls and Serious Injury</h2>
            
            <p>
              According to the United States Center for Disease Control's CDC's 2024 data, falls are the leading 
              cause of injury among older adults:
            </p>
            
            <ul className="blog-stats-list">
              <li>36 million older adults fall each year</li>
              <li>32,000 die from fall injuries annually</li>
              <li>3 million are treated in emergency departments</li>
              <li>$50 billion in medical costs annually</li>
            </ul>
            
            <p>
              But these visible falls are just the tip of the iceberg. Research suggests that for every fall sending 
              a senior to the ER, five to ten go unreported. Why? Shame, fear of losing independence, and minimization 
              all play roles.
            </p>
            
            <p>
              The human impact extends beyond the fall itself. Research shows that lying on the floor for more than 
              an hour leads to serious complications in 50% of cases. After six hours—the time my grandmother spent 
              on that bathroom floor—dehydration, pressure sores, and psychological trauma become likely.
            </p>
          </div>

          {/* Psychological Perspective Section */}
          <div className="blog-psychology-section">
            <h2>Why Falls Multiply: A Psychological Perspective</h2>
            
            <p>
              Loss aversion explains why the fear of falling again often causes more harm than the original fall. 
              Nobel laureate Daniel Kahneman's research shows we feel losses twice as powerfully as gains. Applied 
              to falls:
            </p>
            
            <ul>
              <li>Seniors curtail activities to avoid fall risk (loss of mobility)</li>
              <li>Social isolation increases (loss of connections)</li>
              <li>Physical deconditioning accelerates (loss of strength)</li>
              <li>Depression risk doubles (loss of joy), as many older adults become concerned about their ability 
                  to remain independent after a fall</li>
            </ul>
            
            <p>
              This psychological principle—not just physical injury—drives the multiplication effect.
            </p>
          </div>

          {/* Independence Section */}
          <div className="blog-independence-section">
            <h2 className="blog-centered-heading">Psychology of Aging and Independence</h2>
            
            <h3>The Independence Paradox Revisited</h3>
            
            <p>
              Perhaps no psychological factor is more important to understand than what I call the Independence Paradox—where 
              the very traits that helped someone live independently for decades become barriers to accepting necessary support.
            </p>
            
            <p>
              Consider someone who's been self-reliant for 80 years. Accepting help doesn't just feel uncomfortable; 
              it challenges their core identity. As one woman told me after spending three hours trying to pull herself 
              up rather than call her neighbor: "I didn't want anyone to see me like that."
            </p>
            
            <p>
              Three psychological principles explain this resistance:
            </p>

            <ol className="blog-principles-list">
              <li>
                <strong>Identity Continuity Theory:</strong> We strive to maintain consistent self-image across time. 
                For someone who's been independent for 80 years, accepting help challenges core identity.
              </li>
              <li>
                <strong>Reactance Theory:</strong> When freedom feels threatened, we resist—even against our best interests. 
                This explains why pressure to adopt safety measures often backfires.
              </li>
              <li>
                <strong>Cognitive Dissonance:</strong> When beliefs ("I'm independent") clash with reality ("I fell"), 
                we often adjust perception rather than behavior, minimizing the incident.
              </li>
            </ol>
          </div>

          {/* Cultural Perspectives Section */}
          <div className="blog-cultural-section">
            <h2>Cultural Perspectives on Aging</h2>
            
            <blockquote className="blog-quote">
              "American individualism creates unique challenges. In Japan, accepting help is seen as giving others the 
              gift of being useful. In America, it's often viewed as burden." - Dr. Kenji Tanaka
            </blockquote>
            
            <p>Different cultural approaches:</p>
            
            <ul>
              <li>
                <strong>Asian cultures:</strong> Intergenerational living as norm, although that is changing as evidenced 
                by my time and observations in South Korea. The population is urbanizing and younger people are leaving 
                rural area in droves.
              </li>
              <li>
                <strong>Nordic countries:</strong> Community-based support systems
              </li>
              <li>
                <strong>Latin cultures:</strong> Extended family involvement
              </li>
              <li>
                <strong>American approach:</strong> Technology filling community gaps
              </li>
            </ul>
          </div>

          {/* Family Dynamics Section */}
          <div className="blog-family-section">
            <h2 className="blog-centered-heading">Family Dynamics and Difficult Conversations</h2>
            
            <p>
              Nearly half of adults in their 40s and 50s are caring for both aging parents and their own children. 
              These "sandwich generation" caregivers face unique challenges when discussing fall prevention—balancing 
              respect for parental autonomy with genuine safety concerns.
            </p>

            <h3>Why These Conversations Fail</h3>
            
            <p>
              Most families approach fall prevention talks from a place of fear, leading with statistics and worst-case 
              scenarios. This triggers defensive responses and shuts down dialogue. The key is understanding that you're 
              not really discussing grab bars and medical alerts—you're navigating shifting family roles and confronting 
              mortality.
            </p>

            <h3>Conversation Strategies That Work</h3>
            
            <p>
              Based on interviews with 100+ families who successfully navigated these discussions, timing and framing 
              matter more than what you say:
            </p>

            <h4>Starting the Conversation</h4>
            
            <p>
              Choose a calm moment, not after a fall or health scare. Open with curiosity, not concern:
            </p>

            <blockquote className="blog-conversation-quote">
              "If staying in your home is the goal, what would need to be true for that to work long-term?"
            </blockquote>

            <p>
              This positions you as an ally in maintaining independence, not a threat to it.
            </p>

            <h4>When You Hit Resistance</h4>
            
            <p>
              Resistance is normal—it's not about you. Acknowledge their position without agreeing:
            </p>

            <blockquote className="blog-conversation-quote">
              "It sounds like you're not ready to make changes right now. What kind of situation would make this feel 
              more necessary?"
            </blockquote>

            <p>
              This helps them articulate their own criteria for accepting help, rather than imposing yours.
            </p>

            <h4>Finding Compromise</h4>
            
            <p>
              Sometimes the best approach is to make safety measures temporary:
            </p>

            <blockquote className="blog-conversation-quote">
              "Would you be open to trying it for a short while, just to see how it feels? Not as a commitment, just an experiment?"
            </blockquote>

            <p>
              Many seniors who "try" safety modifications for family peace of mind end up keeping them.
            </p>

            <h3>What Not to Say</h3>
            
            <p>
              Avoid these conversation killers for older people:
            </p>

            <ul className="blog-avoid-list">
              <li>"You're not safe living alone"</li>
              <li>"What if you fall and no one finds you?"</li>
              <li>"You're being stubborn"</li>
              <li>"Do it for me/the grandchildren"</li>
            </ul>

            <p className="blog-remember">
              <strong>Remember:</strong> The goal isn't winning an argument—it's opening an ongoing dialogue about 
              safety and independence.
            </p>
          </div>

          {/* Home Safety Assessment Section */}
          <div className="blog-safety-section">
            <h2 className="blog-centered-heading">Home Safety Assessment</h2>

            <h3>Bathroom (The bathroom accounts for 80% of home falls)</h3>
            
            <ul>
              <li>
                <strong>Grab bars:</strong> Both sides of toilet, inside/outside shower
              </li>
              <li>
                <strong>Lighting:</strong> Use night lights and make sure light switches are accessible from the 
                bathroom entrance and near the shower or toilet to improve safety, especially at night
              </li>
              <li>
                <strong>Surfaces:</strong> Non-slip mats, raised toilet seat
              </li>
              <li>
                <strong>Storage:</strong> Essentials waist-to-shoulder height
              </li>
              <li>
                Wear non-slip footwear in the bathroom to reduce the risk of slipping
              </li>
            </ul>

            <h3>Kitchen</h3>
            
            <ul>
              <li>
                <strong>Reorganize:</strong> Keep food and daily items in easy reach to avoid unnecessary stretching 
                or climbing. Store frequently used pots and utensils on lower shelves
              </li>
              <li>
                <strong>Spills:</strong> Clean up spills immediately to prevent slips and falls, especially in 
                high-traffic areas like the kitchen
              </li>
              <li>
                <strong>Floors:</strong> Replace worn mats immediately and use non-slip mats to reduce fall risk
              </li>
              <li>
                <strong>Footwear:</strong> Wear supportive shoes in the kitchen to provide stability and help prevent falls
              </li>
              <li>
                <strong>Seating:</strong> Use a sturdy stool for prep work to minimize standing for long periods
              </li>
            </ul>

            <h3>Bedroom to Bathroom Path</h3>
            
            <ul>
              <li>
                <strong>Clear:</strong> Keep walking paths between the bedroom and bathroom free of objects and furniture. 
                Remove cords, rugs, and clutter to prevent tripping hazards. Regularly check the path to ensure it 
                remains clear and safe
              </li>
              <li>
                <strong>Lit:</strong> Ensure there is good light along the entire path. Install motion-sensor nightlights 
                and make sure light switches are easily accessible at both ends of the walkway
              </li>
              <li>
                <strong>Supported:</strong> Consider adding a handrail in the hallway for extra support
              </li>
              <li>
                <strong>Direct:</strong> Minimize distance and obstacles. Rearrange furniture to create a straight, 
                unobstructed route
              </li>
            </ul>
          </div>

          {/* Cost-Effective Modifications Section */}
          <div className="blog-cost-section">
            <h2>Cost-Effective Modifications</h2>

            <div className="blog-cost-table">
              <table>
                <thead>
                  <tr>
                    <th>Modification</th>
                    <th>DIY Cost</th>
                    <th>Professional</th>
                    <th>Impact</th>
                    <th>My Pick</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Grab bars</td>
                    <td>$20-50</td>
                    <td>$150-300</td>
                    <td>High</td>
                    <td>Amazon Basics</td>
                  </tr>
                  <tr>
                    <td>Night lights</td>
                    <td>$10-30</td>
                    <td>N/A</td>
                    <td>High</td>
                    <td>Philips Hue Go Smart</td>
                  </tr>
                  <tr>
                    <td>Extra light switches for stairs and hallways</td>
                    <td>$15-40</td>
                    <td>$100-200</td>
                    <td>High</td>
                    <td>Go to a local hardware store or medical equipment store</td>
                  </tr>
                  <tr>
                    <td>Remove or rearrange furniture in walkways</td>
                    <td>Free</td>
                    <td>N/A</td>
                    <td>High</td>
                    <td>NA</td>
                  </tr>
                  <tr>
                    <td>Remove rugs</td>
                    <td>Free</td>
                    <td>N/A</td>
                    <td>High</td>
                    <td>NA</td>
                  </tr>
                  <tr>
                    <td>Raised toilet</td>
                    <td>$30-100</td>
                    <td>$200-400</td>
                    <td>Medium</td>
                    <td>Carex EZ Lock from Walmart</td>
                  </tr>
                  <tr>
                    <td>Handrails for stairs</td>
                    <td>$50-150</td>
                    <td>$300-600</td>
                    <td>Medium</td>
                    <td>EverMark or EZ Handrail from Home Depot</td>
                  </tr>
                  <tr>
                    <td>Ramp</td>
                    <td>$100-300</td>
                    <td>$1000-3000</td>
                    <td>Variable</td>
                    <td>Vevor or Prairie View Industries from Lowes</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>
              For cost-effective safety improvements in your house, consider adding light switches at both the top and 
              bottom of stairs to ensure proper light and reduce fall risk. Rearranging or removing furniture from 
              walkways is another simple way to prevent tripping. As an example, installing motion sensor light switches 
              in hallways or outside walkways can greatly improve visibility at night for a low cost.
            </p>

            <p>
              Consulting an occupational therapist or aging in place specialist can help you identify the most effective 
              and affordable modifications for your home. An occupational therapist can assess your house and recommend 
              personalized changes to improve safety and independence.
            </p>
          </div>

          {/* Fall Proofing Technology Section */}
          <div className="blog-technology-section">
            <h2 className="blog-centered-heading">Fall Proofing Technology Assistive Devices Options and Comparisons</h2>

            <h3>Understanding Your Options</h3>

            <p>
              Technology fills the gap between independence and safety, but options vary widely. Here's how different 
              approaches compare:
            </p>

            {/* Medical Alerts */}
            <div className="tech-option">
              <h4>Medical Alerts</h4>
              
              <p><strong>Pros:</strong> Reliable, professional monitoring, immediate response</p>
              
              <p><strong>Cons:</strong> Stigma, monthly costs, requires wearing device</p>
              
              <p><strong>Best for:</strong> Those living alone, fall history, health conditions</p>
              
              <p>Average response time should be under 45 seconds</p>
              
              <p>
                <strong>Cost:</strong> $25-60/month depending on whether you get one with a landline or if it has 
                cellular and fall detection
              </p>
              
              <p><strong>Companies to consider:</strong> Alert-1, Bay Alarm Medical, Medical Guardian</p>
            </div>

            {/* Smartphone Apps */}
            <div className="tech-option">
              <h4>Smartphone Apps</h4>
              
              <p><strong>Pros:</strong> No additional device, often free, GPS enabled</p>
              
              <p><strong>Cons:</strong> Requires smartphone proficiency, keeping phone charged/nearby</p>
              
              <p><strong>Best for:</strong> Tech-comfortable seniors, active lifestyles</p>
              
              <p><strong>Cost:</strong> Free-$10/month</p>
            </div>

            {/* Smart Home Systems */}
            <div className="tech-option">
              <h4>Smart Home Systems</h4>
              
              <p><strong>Pros:</strong> Passive monitoring, no wearables, integrates with home</p>
              
              <p><strong>Cons:</strong> Higher upfront cost, privacy concerns, tech complexity</p>
              
              <p><strong>Best for:</strong> Tech-forward families, those resistant to wearables</p>
              
              <p><strong>Cost:</strong> $200-1000 setup + $0-50/month</p>
            </div>

            {/* Apple Watch */}
            <div className="tech-option">
              <h4>Apple Watch SE or Apple Watch Series 4 or later</h4>
              
              <p><strong>Pros:</strong> Multiple functions, automatic fall detection, health monitoring</p>
              
              <p><strong>Cons:</strong> Daily charging, expensive, learning curve</p>
              
              <p><strong>Best for:</strong> Active seniors, existing Apple users</p>
              
              <p><strong>Cost:</strong> $279-800 + cellular plan</p>
            </div>

            {/* Provider Comparison Framework */}
            <div className="tech-comparison">
              <h3>Provider Comparison Framework</h3>
              
              <p>When evaluating any solution, consider:</p>

              <h4>Response Process</h4>
              <ul>
                <li>How quickly do they answer?</li>
                <li>Where are operators located?</li>
                <li>What happens if you can't speak?</li>
              </ul>

              <h4>Contract Terms</h4>
              <ul>
                <li>Month-to-month available?</li>
                <li>Cancellation process?</li>
                <li>Hidden fees?</li>
              </ul>

              <h4>Technology Requirements</h4>
              <ul>
                <li>Landline needed?</li>
                <li>Cellular coverage?</li>
                <li>Internet required?</li>
              </ul>

              <h4>Family Features</h4>
              <ul>
                <li>How are loved ones notified?</li>
                <li>Can multiple contacts be added?</li>
                <li>Activity monitoring available?</li>
              </ul>
            </div>
          </div>

          {/* Financial Considerations Section */}
          <div className="blog-financial-section">
            <h2 className="blog-centered-heading">Financial Considerations and the Risk of Falls</h2>

            <h3>The Psychology of Prevention Spending</h3>

            <p>
              Loss aversion also explains why families delay purchasing safety systems, even at $30/month. We see that 
              $360 annual cost as a loss, not investment. Yet the same psychological principle shows why this thinking 
              is flawed: the cost of a fall can contribute to significant financial strain for families, making 
              prevention a wise choice.
            </p>

            <h4>Average fall costs:</h4>

            <ul>
              <li>ER visit: $3,000-10,000</li>
              <li>Hospital stay: $35,000+</li>
              <li>Rehab facility: $20,000+</li>
              <li>Lost family wages: $5,000-25,000</li>
            </ul>

            <p><strong>Total potential cost of one serious fall: $66,000-$140,000</strong></p>

            <p>Compare this to medical alert services at roughly $30-$50 per month</p>

            <p>
              <strong>One prevented fall pays for 10+ years of monitoring.</strong> Health care providers can help 
              families understand coverage options and identify resources to offset these expenses.
            </p>
          </div>

          {/* Financial Resources Section */}
          <div className="blog-resources-section">
            <h2>Financial Resources by Income Level</h2>

            <h3>Lower Income Options:</h3>
            <ul>
              <li>Medicaid may cover medical alerts or a physical therapist in some states. Be forewarned: it doesn't in most cases</li>
              <li>Area Agencies on Aging can provide free assessments. Check near you</li>
              <li>USDA Rural Development Grants: Home modifications up to $7,500</li>
              <li>Medicaid Waivers: Vary by state, may cover alerts and modifications</li>
              <li>VA Aid and Attendance: For veterans, up to $2,000/month</li>
            </ul>

            <h3>Middle Income Strategies:</h3>
            <ul>
              <li>Medicare Advantage often includes alert systems</li>
              <li>FSA/HSA funds may cover devices</li>
              <li>Tax deductions for medical expenses</li>
              <li>Family cost-sharing arrangements</li>
            </ul>

            <h3>Higher Income Considerations:</h3>
            <ul>
              <li>Comprehensive smart home integration</li>
              <li>Private care coordination services</li>
              <li>Proactive PT/OT programs</li>
              <li>Multiple redundant systems</li>
            </ul>
          </div>

          {/* Taking Action Section */}
          <div className="blog-action-roadmap-section">
            <h2 className="blog-centered-heading">Taking Action: Your Personal Roadmap</h2>

            <h3>If You're Just Starting:</h3>
            <p>
              Begin with conversation. Tonight at dinner, ask: "What would it mean to you to age on your own terms?" Listen more than you speak. 
              Understanding their values guides everything else. Encourage them to talk with their health care provider about fall prevention, 
              including ways to improve balance and maintain steady mobility.
            </p>

            <h3>If You've Hit Resistance:</h3>
            <p>
              Step back. Resistance often signals moving too fast. Try one small change—maybe just better lighting. Build trust through small successes.
            </p>

            <h3>If You're Overwhelmed:</h3>
            <p>
              You're not alone. Pick one area—medication review or bathroom safety—and focus there. Simple exercises like sit-to-stand, walking, 
              and balance activities can help strengthen the legs and feet, keep gait steady, and improve breath control. Progress beats perfection.
            </p>

            <h3>If You're Managing from Distance:</h3>
            <p>
              Technology becomes crucial. Start with video calls to assess. Consider professional assessment. Build local support network.
            </p>

            <h3>If Crisis Has Already Struck:</h3>
            <p>
              Focus forward. You can't change what happened, but you can prevent recurrence. Use the event as catalyst for comprehensive planning.
            </p>
          </div>

          {/* Final Thoughts Section */}
          <div className="blog-final-thoughts-section">
            <h2 className="blog-centered-heading">Final Thoughts: Writing New Chapters</h2>

            <p>
              My grandmother lived six meaningful years after that night on the bathroom floor. Like nearly half of adults caring for both aging 
              parents and their own children, I learned that thoughtful supports don't diminish independence—they preserve it.
            </p>

            <p>
              We didn't make her life about injury prevention, but about physical activity and a better life. We helped her make her home safer. We 
              got rid of some throw rugs.
            </p>

            <p>
              She taught me that aging isn't about preventing all risks. It's about choosing which risks are worth taking. With the right supports, 
              those choices expand rather than contract.
            </p>

            <p>
              Every family's journey is unique. Some need comprehensive systems. Others just need a medical alert and good lighting. The key is 
              starting somewhere, adjusting as you go.
            </p>

            <p>Your story and your loved one's story matters. Take one step today.</p>

            <p>Have one conversation. Make one change. Because independence preserved is a gift to everyone who loves them.</p>
          </div>

          {/* Medical Disclaimer and Categories Section */}
          <div className="blog-disclaimer-section">
            <p className="blog-disclaimer">
              <strong>Medical Disclaimer:</strong> This article is for informational purposes only. Always consult with healthcare professionals, occupational 
              therapists, or certified aging-in-place specialists for personalized fall prevention strategies.
            </p>

            <p className="blog-last-updated">Last updated: June 22, 2025</p>

            <div className="blog-categories-box">
              <h3>Alert1 Blog Categories</h3>
              <div className="blog-categories-grid">
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Alert1 Blog</a>
                  <a href="/blog" className="blog-category-link">Family Caregivers</a>
                  <a href="/blog" className="blog-category-link">Resources</a>
                </div>
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Elder Care Planning</a>
                  <a href="/blog" className="blog-category-link">General</a>
                  <a href="/blog" className="blog-category-link">Senior Safety & Independence</a>
                </div>
                <div className="blog-category-column">
                  <a href="/blog" className="blog-category-link">Fall Prevention</a>
                  <a href="/blog" className="blog-category-link">Recipes</a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogFallPreventionPage;

